import React, { useState, useEffect } from 'react';
import {
    FileText, PlusCircle, Search, Download, CheckCircle, AlertCircle,
    Loader2, Send, Printer, FileSpreadsheet, Calendar, Copy, ExternalLink,
    ShieldCheck, Repeat, Clock, AlertTriangle, Edit3, Trash2, Play, Pause,
    DollarSign, Sparkles, Filter, ChevronRight, Check, X, Bell, RefreshCw,
    Ban, Mail, MessageSquare, FileCode
} from 'lucide-react';
import api from '../services/api';
import {
    ATIVIDADES_SERVICOS,
    CST_PIS_COFINS_OPTIONS,
    filtrarAtividadesPorCnae,
    formatarLabelAtividade
} from '../data/fiscalData';

const defaultNextYear = () => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().split('T')[0];
};

const getBrasiliaDate = () => {
    const now = new Date();
    const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
    const brDate = new Date(utc + (3600000 * -3));
    const yyyy = brDate.getFullYear();
    const MM = String(brDate.getMonth() + 1).padStart(2, '0');
    const dd = String(brDate.getDate()).padStart(2, '0');
    return `${yyyy}-${MM}-${dd}`;
};

const getCurrentBrasiliaTime = () => {
    const d = new Date();
    const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Sao_Paulo',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });
    const parts = formatter.formatToParts(d);
    const getPart = type => parts.find(p => p.type === type)?.value || '00';
    let hh = getPart('hour');
    if (hh === '24') hh = '00';
    return `${hh}:${getPart('minute')}:${getPart('second')}`;
};

const NotasFiscaisScreen = () => {
    // Abas de visualização principais: 'notes' | 'recurrences'
    const [activeView, setActiveView] = useState('notes');

    // Estados de Notas Emitidas
    const [notes, setNotes] = useState([]);
    const [loadingNotes, setLoadingNotes] = useState(false);
    const [copiedKey, setCopiedKey] = useState(null);
    const [dateRange, setDateRange] = useState(() => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
        return { start, end };
    });

    // Modais e Ações da NFS-e
    const [danfseModalNote, setDanfseModalNote] = useState(null);
    const [cancelModalNote, setCancelModalNote] = useState(null);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelCode, setCancelCode] = useState('1');
    const [canceling, setCanceling] = useState(false);
    const [correctionModalNote, setCorrectionModalNote] = useState(null);
    const [correctionText, setCorrectionText] = useState('');
    const [savingCorrection, setSavingCorrection] = useState(false);
    const [exportingZip, setExportingZip] = useState(false);

    // Estados de Recorrências Mensais
    const [recurrences, setRecurrences] = useState([]);
    const [loadingRecurrences, setLoadingRecurrences] = useState(false);
    const [editingRecurrence, setEditingRecurrence] = useState(null);
    const [savingRecurrence, setSavingRecurrence] = useState(false);

    // Modal de Emissão / Criação de Recorrência
    const [modalOpen, setModalOpen] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [clientSearch, setClientSearch] = useState('');
    const [showClientDropdown, setShowClientDropdown] = useState(false);
    const [emitting, setEmitting] = useState(false);
    const [configDefaults, setConfigDefaults] = useState({});

    // Form de emissão
    const [form, setForm] = useState({
        payerId: '',
        amount: '',
        description: '',
        codigoAtividadeMunicipal: '',
        itemLc116: '',
        tributacaoNacional: '',
        codigoNbs: '',
        cnae: '',
        aliquota: 2,
        issRetido: false,
        responsavelRetencao: 2,
        obraCode: '',
        art: '',
        naturezaOperacao: '1',
        dataCompetencia: getBrasiliaDate(),
        horaCriacao: getCurrentBrasiliaTime(),
        municipioIncidencia: '',
        estadoIncidencia: '',
        // Tributação Federal (PIS / COFINS)
        cstPisCofins: '01',
        aliquotaPis: 0.65,
        aliquotaCofins: 3.00,
        // Reforma Tributária (CBS & IBS)
        cIndOp: '1',
        indFinal: 1,
        cClassTrib: '000001',
        aliquotaCbs: 0.90,
        aliquotaIbsEst: 0.05,
        aliquotaIbsMun: 0.05,
        aliquotaIbs: 0.10,
        // Recorrência Mensal
        isRecurrent: false,
        dayOfMonth: 10,
        lembreteReajuste: {
            ativo: true,
            dataReajuste: defaultNextYear(),
            mesesIntervalo: 12,
            observacao: 'Reajuste anual de mensalidade (IPCA/IGP-M)'
        },
        saveAsDefault: false
    });

    useEffect(() => {
        loadNotes();
        loadCompanies();
        loadConfig();
        loadRecurrences();
    }, [dateRange]);

    const loadConfig = async () => {
        try {
            const res = await api.get('/integrations');
            if (res.data && res.data.nfse) {
                const n = res.data.nfse;
                setConfigDefaults({
                    ...n,
                    cnaesSecundarios: Array.isArray(n.cnaesSecundarios) ? n.cnaesSecundarios : []
                });
                setForm(prev => ({
                    ...prev,
                    cnae: prev.cnae || n.cnae || '',
                    codigoAtividadeMunicipal: prev.codigoAtividadeMunicipal || n.codigoAtividadeMunicipal || '',
                    itemLc116: prev.itemLc116 || n.itemLc116 || '',
                    tributacaoNacional: prev.tributacaoNacional || n.tributacaoNacional || '',
                    codigoNbs: prev.codigoNbs || n.codigoNbs || '',
                    municipioIncidencia: prev.municipioIncidencia || n.municipioIncidencia || n.codMun || '',
                    estadoIncidencia: prev.estadoIncidencia || n.estadoIncidencia || 'RJ',
                    aliquota: n.aliquota !== undefined ? n.aliquota : prev.aliquota,
                    cstPisCofins: n.cstPisCofins || prev.cstPisCofins,
                    aliquotaPis: n.aliquotaPis !== undefined ? n.aliquotaPis : prev.aliquotaPis,
                    aliquotaCofins: n.aliquotaCofins !== undefined ? n.aliquotaCofins : prev.aliquotaCofins,
                    cIndOp: n.cIndOp || prev.cIndOp,
                    indFinal: n.indFinal !== undefined ? n.indFinal : prev.indFinal,
                    cClassTrib: n.cClassTrib || prev.cClassTrib,
                    aliquotaCbs: n.aliquotaCbs !== undefined ? n.aliquotaCbs : prev.aliquotaCbs,
                    aliquotaIbsEst: n.aliquotaIbsEst !== undefined ? n.aliquotaIbsEst : prev.aliquotaIbsEst,
                    aliquotaIbsMun: n.aliquotaIbsMun !== undefined ? n.aliquotaIbsMun : prev.aliquotaIbsMun,
                    aliquotaIbs: n.aliquotaIbs !== undefined ? n.aliquotaIbs : prev.aliquotaIbs
                }));
            }
        } catch (e) {
            console.error("Erro ao carregar configurações fiscais", e);
        }
    };

    const loadNotes = async () => {
        setLoadingNotes(true);
        try {
            const params = {};
            if (dateRange.start && dateRange.end) {
                params.startDate = dateRange.start;
                params.endDate = dateRange.end;
            }
            const res = await api.get('/nfse', { params });
            setNotes(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error("Erro ao carregar notas fiscais:", error);
        } finally {
            setLoadingNotes(false);
        }
    };

    const loadRecurrences = async () => {
        setLoadingRecurrences(true);
        try {
            const res = await api.get('/nfse/recurrences');
            setRecurrences(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error("Erro ao carregar recorrências:", error);
        } finally {
            setLoadingRecurrences(false);
        }
    };

    const loadCompanies = async () => {
        try {
            const res = await api.get('/external-companies?limit=1000');
            if (res.data && Array.isArray(res.data.data)) {
                setCompanies(res.data.data);
            } else if (Array.isArray(res.data)) {
                setCompanies(res.data);
            } else {
                setCompanies([]);
            }
        } catch (e) {
            console.error("Erro ao buscar empresas:", e);
        }
    };

    // Seleção de atividade municipal preenche automaticamente LC 116, Tributação Nacional, NBS e descrição
    const handleActivitySelect = (codigoAtividadeMunicipal) => {
        const act = ATIVIDADES_SERVICOS.find(a => a.codigoAtividadeMunicipal === codigoAtividadeMunicipal);
        if (!act) return;
        setForm(prev => ({
            ...prev,
            codigoAtividadeMunicipal: act.codigoAtividadeMunicipal,
            itemLc116: act.itemLc116,
            tributacaoNacional: act.tributacaoNacional,
            codigoNbs: act.codigoNbs,
            cnae: (act.cnaes && act.cnaes[0]) ? act.cnaes[0] : prev.cnae,
            aliquota: act.aliquotaIssSugerida !== undefined ? act.aliquotaIssSugerida : prev.aliquota,
            description: prev.description ? prev.description : act.descricao
        }));
    };

    const handleModelSelect = (e) => {
        const modelName = e.target.value;
        if (!modelName) return;
        const model = (configDefaults.models || []).find(m => m.name === modelName);
        if (model) {
            setForm(prev => ({
                ...prev,
                codigoAtividadeMunicipal: model.codigoAtividadeMunicipal || prev.codigoAtividadeMunicipal,
                itemLc116: model.itemLc116 || prev.itemLc116,
                tributacaoNacional: model.tributacaoNacional || prev.tributacaoNacional,
                codigoNbs: model.codigoNbs || prev.codigoNbs,
                cnae: model.cnae || prev.cnae,
                aliquota: model.aliquota !== undefined ? model.aliquota : prev.aliquota,
                issRetido: model.issRetido !== undefined ? model.issRetido : prev.issRetido,
                responsavelRetencao: model.responsavelRetencao || prev.responsavelRetencao,
                obraCode: model.obraCode || prev.obraCode,
                art: model.art || prev.art,
                naturezaOperacao: model.naturezaOperacao || prev.naturezaOperacao,
                municipioIncidencia: model.municipioIncidencia || prev.municipioIncidencia,
                estadoIncidencia: model.estadoIncidencia || prev.estadoIncidencia,
                cstPisCofins: model.cstPisCofins || prev.cstPisCofins,
                aliquotaPis: model.aliquotaPis !== undefined ? model.aliquotaPis : prev.aliquotaPis,
                aliquotaCofins: model.aliquotaCofins !== undefined ? model.aliquotaCofins : prev.aliquotaCofins,
                cIndOp: model.cIndOp || prev.cIndOp,
                indFinal: model.indFinal !== undefined ? model.indFinal : prev.indFinal,
                cClassTrib: model.cClassTrib || prev.cClassTrib,
                aliquotaCbs: model.aliquotaCbs !== undefined ? model.aliquotaCbs : prev.aliquotaCbs,
                aliquotaIbsEst: model.aliquotaIbsEst !== undefined ? model.aliquotaIbsEst : prev.aliquotaIbsEst,
                aliquotaIbsMun: model.aliquotaIbsMun !== undefined ? model.aliquotaIbsMun : prev.aliquotaIbsMun,
                aliquotaIbs: model.aliquotaIbs !== undefined ? model.aliquotaIbs : prev.aliquotaIbs,
                description: model.description || prev.description
            }));
        }
    };

    const handleSaveAsModel = async () => {
        const name = prompt("Nome para salvar este modelo de emissão (ex: Desenvolvimento de Software, Suporte Técnico):");
        if (!name) return;

        try {
            const newModel = {
                name,
                codigoAtividadeMunicipal: form.codigoAtividadeMunicipal,
                itemLc116: form.itemLc116,
                tributacaoNacional: form.tributacaoNacional,
                codigoNbs: form.codigoNbs,
                cnae: form.cnae,
                aliquota: form.aliquota,
                issRetido: form.issRetido,
                responsavelRetencao: form.responsavelRetencao,
                obraCode: form.obraCode,
                art: form.art,
                naturezaOperacao: form.naturezaOperacao,
                municipioIncidencia: form.municipioIncidencia,
                estadoIncidencia: form.estadoIncidencia,
                cstPisCofins: form.cstPisCofins,
                aliquotaPis: form.aliquotaPis,
                aliquotaCofins: form.aliquotaCofins,
                cIndOp: form.cIndOp,
                indFinal: form.indFinal,
                cClassTrib: form.cClassTrib,
                aliquotaCbs: form.aliquotaCbs,
                aliquotaIbsEst: form.aliquotaIbsEst,
                aliquotaIbsMun: form.aliquotaIbsMun,
                aliquotaIbs: form.aliquotaIbs,
                description: form.description
            };

            await api.post('/nfse/models', { model: newModel });
            alert(`Modelo "${name}" salvo com sucesso!`);
            loadConfig();
        } catch (error) {
            alert("Erro ao salvar modelo: " + (error.response?.data?.error || error.message));
        }
    };

    const handleEmit = async () => {
        if (!form.payerId || !form.amount || !form.description) {
            return alert("Por favor, selecione o Tomador e preencha o Valor e a Descrição do serviço.");
        }
        setEmitting(true);
        try {
            const payload = {
                ...form,
                codigoAtividadeMunicipal: '',
                dataCompetencia: form.dataCompetencia || getBrasiliaDate(),
                horaCriacao: form.horaCriacao || getCurrentBrasiliaTime(),
                cClassTrib: '000001',
                cstIbsCbs: '000',
                municipioIncidencia: form.municipioIncidencia || configDefaults.codMun || '',
                serviceCode: form.tributacaoNacional || form.itemLc116
            };
            const res = await api.post('/nfse', payload);

            if (form.isRecurrent) {
                alert("NFS-e emitida e Recorrência Mensal programada com sucesso! Todo mês a nota será gerada automaticamente.");
            } else {
                alert("NFS-e enviada com sucesso para autorização!");
            }

            setModalOpen(false);
            loadNotes();
            if (form.isRecurrent) loadRecurrences();
        } catch (error) {
            alert("Erro ao emitir NFS-e: " + (error.response?.data?.error || error.message));
        } finally {
            setEmitting(false);
        }
    };

    // 🆕 Ações de NFS-e (Download XML, WhatsApp, Email, Cancelamento e Retificação)
    const handleExportXmlsZip = async () => {
        if (!notes || notes.length === 0) {
            return alert("Nenhuma nota fiscal encontrada no período selecionado para exportar XMLs.");
        }
        setExportingZip(true);
        try {
            const params = {
                startDate: dateRange.start,
                endDate: dateRange.end
            };
            const res = await api.get('/nfse/batch/xmls-zip', {
                params,
                responseType: 'blob'
            });

            const blob = new Blob([res.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const startLabel = dateRange.start ? dateRange.start.replace(/-/g, '') : 'todas';
            const endLabel = dateRange.end ? `_a_${dateRange.end.replace(/-/g, '')}` : '';
            link.setAttribute('download', `NFSe_XMLs_${startLabel}${endLabel}.zip`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            alert("Erro ao exportar XMLs em ZIP: " + (error.response?.data?.error || error.message));
        } finally {
            setExportingZip(false);
        }
    };

    const handleExportBatchDanfse = () => {
        if (!notes || notes.length === 0) {
            return alert("Nenhuma nota fiscal encontrada no período selecionado para exportar PDFs.");
        }
        const token = localStorage.getItem('token');
        const start = encodeURIComponent(dateRange.start || '');
        const end = encodeURIComponent(dateRange.end || '');
        const win = window.open(`/api/nfse/batch/pdf?startDate=${start}&endDate=${end}&token=${token}`, '_blank');
        if (win) win.focus();
    };

    const handleDownloadXml = async (note) => {
        try {
            const res = await api.get(`/nfse/${note._id}/xml`, { responseType: 'blob' });
            const blob = new Blob([res.data], { type: 'application/xml' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `NFSe_${note.number || note.numeroDps || note.chaveAcesso || note._id}.xml`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (e) {
            alert("Erro ao baixar XML da NFS-e: " + (e.response?.data?.error || e.message));
        }
    };

    const handleSendWhatsApp = async (note) => {
        const phone = prompt("Confirme o telefone do tomador para envio (com DDD):", note.payerId?.phone || '');
        if (!phone) return;
        try {
            const res = await api.post(`/nfse/${note._id}/send-whatsapp`, { phone });
            alert(res.data?.message || "NFS-e enviada via WhatsApp com sucesso!");
        } catch (e) {
            alert("Erro ao enviar por WhatsApp: " + (e.response?.data?.error || e.message));
        }
    };

    const handleSendEmail = async (note) => {
        const email = prompt("Confirme o e-mail do tomador para envio:", note.payerId?.email || '');
        if (!email) return;
        try {
            const res = await api.post(`/nfse/${note._id}/send-email`, { email });
            alert(res.data?.message || "NFS-e enviada por e-mail com sucesso!");
        } catch (e) {
            alert("Erro ao enviar por e-mail: " + (e.response?.data?.error || e.message));
        }
    };

    const handleConfirmCancel = async () => {
        if (!cancelReason || cancelReason.trim().length < 5) {
            return alert("Por favor, descreva o motivo do cancelamento (mínimo 5 caracteres).");
        }
        setCanceling(true);
        try {
            await api.post(`/nfse/${cancelModalNote._id}/cancel`, {
                motivo: cancelReason,
                codigoMotivo: cancelCode
            });
            alert("NFS-e cancelada com sucesso!");
            setCancelModalNote(null);
            setCancelReason('');
            loadNotes();
        } catch (e) {
            alert("Erro ao cancelar NFS-e: " + (e.response?.data?.error || e.message));
        } finally {
            setCanceling(false);
        }
    };

    const handleConfirmCorrection = async () => {
        if (!correctionText || correctionText.trim().length < 5) {
            return alert("Por favor, informe a descrição da retificação (mínimo 5 caracteres).");
        }
        setSavingCorrection(true);
        try {
            await api.post(`/nfse/${correctionModalNote._id}/correction`, {
                descricao: correctionText
            });
            alert("Carta de correção / retificação registrada com sucesso!");
            setCorrectionModalNote(null);
            setCorrectionText('');
            loadNotes();
        } catch (e) {
            alert("Erro ao registrar retificação: " + (e.response?.data?.error || e.message));
        } finally {
            setSavingCorrection(false);
        }
    };

    // Ações de Recorrência
    const handleToggleRecurrenceStatus = async (rec) => {
        const newStatus = rec.status === 'active' ? 'paused' : 'active';
        try {
            await api.put(`/nfse/recurrences/${rec._id}`, { status: newStatus });
            loadRecurrences();
        } catch (e) {
            alert("Erro ao alterar status da recorrência: " + e.message);
        }
    };

    const handleTriggerRecurrence = async (rec) => {
        if (!window.confirm(`Deseja disparar AGORA a emissão da NFS-e para ${rec.payerName || rec.payerId?.name} no valor de R$ ${rec.amount?.toFixed(2)}?`)) {
            return;
        }
        try {
            const res = await api.post(`/nfse/recurrences/${rec._id}/trigger`);
            alert("NFS-e emitida com sucesso a partir da recorrência!");
            loadNotes();
            loadRecurrences();
        } catch (e) {
            alert("Erro ao disparar emissão da recorrência: " + (e.response?.data?.error || e.message));
        }
    };

    const handleDeleteRecurrence = async (id) => {
        if (!window.confirm("Tem certeza que deseja cancelar esta recorrência mensal? Nenhuma nota futura será gerada.")) {
            return;
        }
        try {
            await api.delete(`/nfse/recurrences/${id}`);
            loadRecurrences();
        } catch (e) {
            alert("Erro ao excluir recorrência: " + e.message);
        }
    };

    const openEditRecurrenceModal = (rec) => {
        setEditingRecurrence({
            _id: rec._id,
            payerName: rec.payerName || rec.payerId?.name || 'Cliente',
            amount: rec.amount,
            dayOfMonth: rec.dayOfMonth || 10,
            description: rec.description || '',
            lembreteAtivo: rec.lembreteReajuste?.ativo || false,
            dataReajuste: rec.lembreteReajuste?.dataReajuste ? rec.lembreteReajuste.dataReajuste.split('T')[0] : defaultNextYear(),
            mesesIntervalo: rec.lembreteReajuste?.mesesIntervalo || 12,
            observacao: rec.lembreteReajuste?.observacao || 'Reajuste anual de mensalidade'
        });
    };

    const handleSaveEditedRecurrence = async () => {
        if (!editingRecurrence) return;
        setSavingRecurrence(true);
        try {
            await api.put(`/nfse/recurrences/${editingRecurrence._id}`, {
                amount: parseFloat(editingRecurrence.amount),
                dayOfMonth: parseInt(editingRecurrence.dayOfMonth),
                description: editingRecurrence.description,
                lembreteReajuste: {
                    ativo: editingRecurrence.lembreteAtivo,
                    dataReajuste: editingRecurrence.dataReajuste,
                    mesesIntervalo: parseInt(editingRecurrence.mesesIntervalo) || 12,
                    observacao: editingRecurrence.observacao,
                    notificado: false
                }
            });
            alert("Recorrência e reajuste atualizados com sucesso!");
            setEditingRecurrence(null);
            loadRecurrences();
        } catch (e) {
            alert("Erro ao atualizar recorrência: " + e.message);
        } finally {
            setSavingRecurrence(false);
        }
    };

    // Verificações de Reajuste
    const isReadjustmentDue = (rec) => {
        if (!rec.lembreteReajuste?.ativo || !rec.lembreteReajuste?.dataReajuste) return false;
        const target = new Date(rec.lembreteReajuste.dataReajuste);
        const now = new Date();
        const diffDays = Math.ceil((target - now) / (1000 * 60 * 60 * 24));
        return diffDays <= 30;
    };

    const isReadjustmentExpired = (rec) => {
        if (!rec.lembreteReajuste?.ativo || !rec.lembreteReajuste?.dataReajuste) return false;
        const target = new Date(rec.lembreteReajuste.dataReajuste);
        const now = new Date();
        return target <= now;
    };

    const dueRecurrences = recurrences.filter(isReadjustmentDue);

    // Lista de atividades filtradas OBRIGATORIAMENTE pelos CNAEs do Prestador (CNPJ)
    const allPrestadorCnaes = [configDefaults.cnae, ...(configDefaults.cnaesSecundarios || [])].filter(Boolean);
    const atividadesCnae = allPrestadorCnaes.length > 0 ? filtrarAtividadesPorCnae(allPrestadorCnaes) : [];
    const atividadesDisponiveis = atividadesCnae.length > 0 ? atividadesCnae : ATIVIDADES_SERVICOS;

    // Cálculos em tempo real para o modal de emissão
    const formAmountNum = parseFloat(form.amount) || 0;
    const formPisVal = (formAmountNum * (parseFloat(form.aliquotaPis) || 0)) / 100;
    const formCofinsVal = (formAmountNum * (parseFloat(form.aliquotaCofins) || 0)) / 100;
    const formCbsVal = (formAmountNum * (parseFloat(form.aliquotaCbs) || 0)) / 100;
    const formIbsAliqTotal = (parseFloat(form.aliquotaIbsEst) || 0) + (parseFloat(form.aliquotaIbsMun) || 0);
    const formIbsVal = (formAmountNum * formIbsAliqTotal) / 100;

    const getStatusBadge = (status) => {
        switch (status) {
            case 'authorized':
                return <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold flex items-center"><CheckCircle className="w-3 h-3 mr-1" /> Autorizada</span>;
            case 'processing':
                return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-bold flex items-center"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Processando</span>;
            case 'error':
                return <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold flex items-center"><AlertCircle className="w-3 h-3 mr-1" /> Erro</span>;
            case 'canceled':
                return <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-bold flex items-center"><FileText className="w-3 h-3 mr-1" /> Cancelada</span>;
            default:
                return <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">{status}</span>;
        }
    };

    const handlePrintList = () => {
        const printWindow = window.open('', '_blank', 'width=900,height=600');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Relatório de Notas Fiscais</title>
                    <style>
                        body { font-family: sans-serif; padding: 20px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
                        th { background-color: #f2f2f2; }
                        .header { margin-bottom: 20px; border-bottom: 2px solid #ccc; padding-bottom: 10px; }
                        h1 { margin: 0; font-size: 18px; }
                        .summary { margin-top: 20px; font-weight: bold; text-align: right; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>Relatório de Notas Fiscais (NFS-e)</h1>
                        <p>Período: ${dateRange.start ? new Date(dateRange.start).toLocaleDateString() : 'Início'} a ${dateRange.end ? new Date(dateRange.end).toLocaleDateString() : 'Fim'}</p>
                        <p>Gerado em: ${new Date().toLocaleString()}</p>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>Número / DPS</th>
                                <th>Tomador</th>
                                <th>Emissão</th>
                                <th>Status</th>
                                <th style="text-align: right;">Valor</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${notes.map(note => `
                                <tr>
                                    <td>${note.number ? `Nº ${note.number}` : (note.numeroDps ? `DPS ${note.numeroDps}` : 'Pendente')}</td>
                                    <td>${note.payerId?.name || 'Desconhecido'}</td>
                                    <td>${new Date(note.issueDate).toLocaleDateString()}</td>
                                    <td>${note.status === 'authorized' ? 'AUTORIZADA' : (note.status === 'canceled' ? 'CANCELADA' : 'PROCESSANDO')}</td>
                                    <td style="text-align: right;">R$ ${note.amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                    <div class="summary">
                        Total: R$ ${notes.reduce((acc, curr) => acc + (curr.amount || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </div>
                    <script>window.onload = function() { window.print(); }</script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    const handleExportExcel = () => {
        const headers = ["Número", "DPS", "Tomador", "CNPJ Tomador", "Emissão", "Status", "Valor", "PIS", "COFINS", "CBS", "IBS"];
        const rows = notes.map(n => [
            n.number || '',
            n.numeroDps || '',
            n.payerId?.name || '',
            n.payerId?.cnpj || '',
            new Date(n.issueDate).toLocaleDateString(),
            n.status,
            (n.amount || 0).toFixed(2).replace('.', ','),
            (n.valorPis || 0).toFixed(2).replace('.', ','),
            (n.valorCofins || 0).toFixed(2).replace('.', ','),
            (n.valorCbs || 0).toFixed(2).replace('.', ','),
            (n.valorIbs || 0).toFixed(2).replace('.', ',')
        ]);

        const csvContent = [headers.join(";"), ...rows.map(e => e.join(";"))].join("\n");
        const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `nfse_relatorio_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const totalFaturamentoRecorrente = recurrences
        .filter(r => r.status === 'active')
        .reduce((sum, r) => sum + (r.amount || 0), 0);

    return (
        <div className="flex-1 flex flex-col h-full bg-gray-50 p-6 overflow-hidden">
            {/* TOPO: TÍTULO, ABAS E BOTÃO EMITIR */}
            <div className="flex flex-col gap-4 mb-5">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
                            <FileText className="mr-2 text-blue-600" /> Notas Fiscais (NFS-e)
                        </h1>
                        <p className="text-sm text-gray-500">
                            Emissão Padrão Nacional (SEFIN), modelos inteligentes e faturamento recorrente mensal
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => {
                                setForm(prev => ({ ...prev, isRecurrent: false, dataCompetencia: getBrasiliaDate(), horaCriacao: getCurrentBrasiliaTime() }));
                                setModalOpen(true);
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-bold flex items-center shadow-md transition-all text-sm"
                        >
                            <PlusCircle className="mr-2 w-4 h-4" /> Emitir NFS-e
                        </button>
                    </div>
                </div>

                {/* ABAS SUPERIORES: NOTAS EMITIDAS VS RECORRÊNCIAS MENSAIS */}
                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => setActiveView('notes')}
                        className={`flex items-center px-5 py-3 text-sm font-bold border-b-2 transition-colors ${
                            activeView === 'notes'
                                ? 'border-blue-600 text-blue-700 bg-blue-50/50'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100/50'
                        }`}
                    >
                        <FileText className="w-4 h-4 mr-2" />
                        Notas Emitidas
                        <span className="ml-2 bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs font-semibold">
                            {notes.length}
                        </span>
                    </button>

                    <button
                        onClick={() => setActiveView('recurrences')}
                        className={`flex items-center px-5 py-3 text-sm font-bold border-b-2 transition-colors ${
                            activeView === 'recurrences'
                                ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100/50'
                        }`}
                    >
                        <Repeat className="w-4 h-4 mr-2" />
                        Recorrências Mensais
                        <span className="ml-2 bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                            {recurrences.length}
                        </span>
                        {dueRecurrences.length > 0 && (
                            <span className="ml-2 bg-red-500 text-white px-2 py-0.5 rounded-full text-[11px] font-bold animate-pulse flex items-center">
                                <AlertTriangle className="w-3 h-3 mr-1" /> {dueRecurrences.length} reajuste(s)
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* CONTEÚDO DA ABA 1: NOTAS EMITIDAS */}
            {activeView === 'notes' && (
                <div className="flex-1 flex flex-col min-h-0 space-y-4">
                    {/* Barra de Filtros */}
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-3 rounded-lg shadow-sm border shrink-0">
                        <div className="flex items-center gap-2">
                            <Calendar className="text-gray-400 w-5 h-5" />
                            <span className="text-sm font-bold text-gray-600">Período:</span>
                            <input
                                type="date"
                                className="border rounded p-1.5 text-sm text-gray-600 outline-none focus:border-blue-500 transition-all"
                                value={dateRange.start}
                                onChange={e => setDateRange({ ...dateRange, start: e.target.value })}
                            />
                            <span className="text-gray-400">-</span>
                            <input
                                type="date"
                                className="border rounded p-1.5 text-sm text-gray-600 outline-none focus:border-blue-500 transition-all"
                                value={dateRange.end}
                                onChange={e => setDateRange({ ...dateRange, end: e.target.value })}
                            />
                            <button
                                onClick={loadNotes}
                                className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                                title="Recarregar notas"
                            >
                                <RefreshCw className={`w-4 h-4 ${loadingNotes ? 'animate-spin' : ''}`} />
                            </button>
                        </div>

                        <div className="flex gap-2 flex-wrap items-center">
                            <button
                                onClick={handleExportXmlsZip}
                                disabled={exportingZip || notes.length === 0}
                                className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg font-bold flex items-center transition-colors text-sm shadow-sm"
                                title="Exportar todos os XMLs das notas do período em arquivo .ZIP"
                            >
                                {exportingZip ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileCode className="w-4 h-4 mr-2" />}
                                Exportar XMLs (ZIP)
                            </button>
                            <button
                                onClick={handleExportBatchDanfse}
                                disabled={notes.length === 0}
                                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg font-bold flex items-center transition-colors text-sm shadow-sm"
                                title="Visualizar e imprimir todos os PDFs / DANFSes das notas do período"
                            >
                                <FileText className="w-4 h-4 mr-2" /> Exportar PDFs
                            </button>
                            <button
                                onClick={handlePrintList}
                                className="bg-white border hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg font-bold flex items-center transition-colors text-sm shadow-sm"
                                title="Imprimir Lista"
                            >
                                <Printer className="w-4 h-4 mr-2" /> Imprimir
                            </button>
                            <button
                                onClick={handleExportExcel}
                                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg font-bold flex items-center transition-colors text-sm shadow-sm"
                                title="Exportar CSV"
                            >
                                <FileSpreadsheet className="w-4 h-4 mr-2" /> Exportar CSV
                            </button>
                        </div>
                    </div>

                    {/* Tabela de Notas */}
                    <div className="bg-white rounded-xl shadow-sm border overflow-hidden flex-1 overflow-y-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-50 text-gray-600 uppercase font-bold border-b sticky top-0 z-10">
                                <tr>
                                    <th className="px-6 py-3">Número / DPS</th>
                                    <th className="px-6 py-3">Tomador</th>
                                    <th className="px-6 py-3">Emissão</th>
                                    <th className="px-6 py-3 text-right">Valor Total</th>
                                    <th className="px-6 py-3 text-center">Status</th>
                                    <th className="px-6 py-3 text-center">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {loadingNotes ? (
                                    <tr>
                                        <td colSpan="6" className="p-8 text-center text-gray-500">
                                            <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-600 mb-2" />
                                            Carregando notas fiscais...
                                        </td>
                                    </tr>
                                ) : notes.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="p-8 text-center text-gray-500">
                                            Nenhuma nota fiscal emitida no período selecionado.
                                        </td>
                                    </tr>
                                ) : (
                                    notes.map(note => (
                                        <tr key={note._id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="font-mono font-bold text-gray-800">
                                                    {note.number ? `Nº ${note.number}` : (note.numeroDps ? `DPS ${note.numeroDps}` : 'Pendente')}
                                                </div>
                                                {note.chaveAcesso && (
                                                    <div className="flex items-center text-[11px] text-gray-400 font-mono mt-1" title={note.chaveAcesso}>
                                                        <span className="truncate max-w-[130px]">{note.chaveAcesso.slice(0, 16)}...</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(note.chaveAcesso);
                                                                setCopiedKey(note._id);
                                                                setTimeout(() => setCopiedKey(null), 2000);
                                                            }}
                                                            className="ml-1 text-blue-500 hover:text-blue-700 inline-flex items-center"
                                                            title="Copiar Chave de Acesso Oficial (50 dígitos)"
                                                        >
                                                            {copiedKey === note._id ? <span className="text-green-600 font-bold">Copiado!</span> : <Copy className="w-3 h-3" />}
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 font-medium">
                                                <div className="text-gray-900 font-bold">{note.payerId?.name || 'Desconhecido'}</div>
                                                {note.payerId?.cnpj && <div className="text-xs text-gray-400 font-mono">{note.payerId.cnpj}</div>}
                                            </td>
                                            <td className="px-6 py-4 text-gray-500">{new Date(note.issueDate).toLocaleDateString()}</td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="font-bold text-gray-800">
                                                    R$ {note.amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                </div>
                                                {(note.valorCbs > 0 || note.valorIbs > 0 || note.valorPis > 0) && (
                                                    <div className="text-[10px] text-indigo-600 font-medium">
                                                        {note.valorPis > 0 && `PIS: R$ ${note.valorPis.toFixed(2)} | `}
                                                        CBS: R$ {(note.valorCbs || 0).toFixed(2)} | IBS: R$ {(note.valorIbs || 0).toFixed(2)}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 flex justify-center">{getStatusBadge(note.status)}</td>
                                            <td className="px-6 py-4 text-center">
                                                <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                                    {/* Visualizar / Imprimir DANFSe v2.0 */}
                                                    <button
                                                        type="button"
                                                        onClick={() => setDanfseModalNote(note)}
                                                        className="px-2 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded text-xs font-bold inline-flex items-center gap-1 border border-blue-200 transition-colors shadow-sm"
                                                        title="Visualizar e Imprimir DANFSe v2.0 Oficial"
                                                    >
                                                        <Printer className="w-3.5 h-3.5 text-blue-600" /> DANFSe
                                                    </button>

                                                    {/* Baixar XML */}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDownloadXml(note)}
                                                        className="px-2 py-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded text-xs font-bold inline-flex items-center gap-1 border border-emerald-200 transition-colors shadow-sm"
                                                        title="Baixar Arquivo XML da NFS-e"
                                                    >
                                                        <FileCode className="w-3.5 h-3.5 text-emerald-600" /> XML
                                                    </button>

                                                    {/* Enviar WhatsApp */}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSendWhatsApp(note)}
                                                        className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                                                        title="Enviar NFS-e por WhatsApp"
                                                    >
                                                        <MessageSquare className="w-4 h-4" />
                                                    </button>

                                                    {/* Enviar E-mail */}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSendEmail(note)}
                                                        className="p-1 text-amber-600 hover:bg-amber-50 rounded transition-colors"
                                                        title="Enviar NFS-e por E-mail"
                                                    >
                                                        <Mail className="w-4 h-4" />
                                                    </button>

                                                    {/* Cancelar NFS-e */}
                                                    {note.status !== 'canceled' && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setCancelModalNote(note);
                                                                setCancelReason('');
                                                                setCancelCode('1');
                                                            }}
                                                            className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                                            title="Cancelar NFS-e"
                                                        >
                                                            <Ban className="w-4 h-4" />
                                                        </button>
                                                    )}

                                                    {/* Carta de Correção */}
                                                    {note.status !== 'canceled' && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setCorrectionModalNote(note);
                                                                setCorrectionText('');
                                                            }}
                                                            className="p-1 text-gray-500 hover:bg-gray-100 rounded transition-colors"
                                                            title="Registrar Carta de Correção / Retificação"
                                                        >
                                                            <Edit3 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* CONTEÚDO DA ABA 2: RECORRÊNCIAS MENSAIS */}
            {activeView === 'recurrences' && (
                <div className="flex-1 flex flex-col min-h-0 space-y-4">
                    {/* CARDS DE RESUMO DE FATURAMENTO RECORRENTE */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 shrink-0">
                        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
                            <div>
                                <span className="text-xs font-bold text-gray-500 uppercase">Recorrências Ativas</span>
                                <div className="text-2xl font-black text-gray-800 mt-1">
                                    {recurrences.filter(r => r.status === 'active').length}
                                </div>
                            </div>
                            <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
                                <Repeat className="w-6 h-6" />
                            </div>
                        </div>

                        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
                            <div>
                                <span className="text-xs font-bold text-gray-500 uppercase">Faturamento Mensal Programado</span>
                                <div className="text-2xl font-black text-green-600 mt-1">
                                    R$ {totalFaturamentoRecorrente.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </div>
                            </div>
                            <div className="p-3 bg-green-50 text-green-600 rounded-lg">
                                <DollarSign className="w-6 h-6" />
                            </div>
                        </div>

                        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
                            <div>
                                <span className="text-xs font-bold text-gray-500 uppercase">Lembretes de Reajuste</span>
                                <div className="text-2xl font-black text-amber-600 mt-1 flex items-center">
                                    {dueRecurrences.length}
                                    {dueRecurrences.length > 0 && (
                                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-bold ml-2">
                                            Ação Necessária
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="p-3 bg-amber-50 text-amber-600 rounded-lg">
                                <Bell className="w-6 h-6" />
                            </div>
                        </div>
                    </div>

                    {/* BANNER DE ALERTA DE REAJUSTE DE MENSALIDADE */}
                    {dueRecurrences.length > 0 && (
                        <div className="bg-gradient-to-r from-amber-50 to-red-50 border-l-4 border-amber-500 p-4 rounded-r-xl shadow-sm shrink-0 flex items-center justify-between">
                            <div className="flex items-center">
                                <AlertTriangle className="w-6 h-6 text-amber-600 mr-3 shrink-0" />
                                <div>
                                    <h4 className="text-sm font-bold text-amber-900">
                                        Lembrete: {dueRecurrences.length} mensalidade(s) no período de reajuste anual!
                                    </h4>
                                    <p className="text-xs text-amber-800 mt-0.5">
                                        A data prevista para o reajuste anual de contrato chegou ou está próxima. Revise e atualize os valores das mensalidades para as próximas emissões.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TABELA DE RECORRÊNCIAS */}
                    <div className="bg-white rounded-xl shadow-sm border overflow-hidden flex-1 overflow-y-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-50 text-gray-600 uppercase font-bold border-b sticky top-0 z-10">
                                <tr>
                                    <th className="px-6 py-3">Tomador (Cliente)</th>
                                    <th className="px-6 py-3">Dia da Emissão</th>
                                    <th className="px-6 py-3">Serviço / Atividade</th>
                                    <th className="px-6 py-3 text-right">Valor Mensal</th>
                                    <th className="px-6 py-3 text-center">Status</th>
                                    <th className="px-6 py-3 text-center">Lembrete de Reajuste</th>
                                    <th className="px-6 py-3 text-center">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {loadingRecurrences ? (
                                    <tr>
                                        <td colSpan="7" className="p-8 text-center text-gray-500">
                                            <Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-600 mb-2" />
                                            Carregando recorrências mensais...
                                        </td>
                                    </tr>
                                ) : recurrences.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="p-8 text-center text-gray-500">
                                            <p className="font-bold text-gray-700 mb-1">Nenhuma recorrência mensal cadastrada.</p>
                                            <p className="text-xs text-gray-400">
                                                Ao emitir uma nota fiscal, marque a opção "Ativar Emissão Recorrente Todo Mês" para automatizar a emissão contínua.
                                            </p>
                                        </td>
                                    </tr>
                                ) : (
                                    recurrences.map(rec => {
                                        const due = isReadjustmentDue(rec);
                                        const expired = isReadjustmentExpired(rec);
                                        return (
                                            <tr key={rec._id} className={`hover:bg-gray-50 transition-colors ${due ? 'bg-amber-50/40' : ''}`}>
                                                <td className="px-6 py-4 font-medium">
                                                    <div className="text-gray-900 font-bold">{rec.payerName || rec.payerId?.name || 'Cliente'}</div>
                                                    {rec.payerId?.cnpj && <div className="text-xs text-gray-400 font-mono">{rec.payerId.cnpj}</div>}
                                                    {(rec.linkBoleto || rec.recurrenceId) && (
                                                        <span className="inline-flex items-center gap-1 text-[11px] bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full font-semibold mt-1">
                                                            💳 Boleto Recorrente Vinculado
                                                        </span>
                                                    )}
                                                </td>

                                                <td className="px-6 py-4">
                                                    <span className="inline-flex items-center text-sm font-bold text-gray-700 bg-gray-100 px-2.5 py-1 rounded">
                                                        <Clock className="w-3.5 h-3.5 mr-1 text-gray-500" />
                                                        Todo dia {String(rec.dayOfMonth).padStart(2, '0')}
                                                    </span>
                                                </td>

                                                <td className="px-6 py-4 text-xs text-gray-600 max-w-[200px]">
                                                    <div className="font-semibold text-gray-800 truncate" title={rec.description}>
                                                        {rec.description || 'Prestação de Serviços'}
                                                    </div>
                                                    <div className="text-[11px] text-gray-400">
                                                        LC 116: {rec.itemLc116 || '-'} | Ativ: {rec.codigoAtividadeMunicipal || '-'}
                                                    </div>
                                                </td>

                                                <td className="px-6 py-4 text-right">
                                                    <div className="font-black text-gray-900 text-base">
                                                        R$ {rec.amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                    </div>
                                                    <span className="text-[10px] text-gray-400 uppercase">Mensal</span>
                                                    {(rec.linkBoleto || rec.recurrenceId) && (
                                                        <span className="text-[10px] text-blue-600 block font-medium">Sincronizado c/ Boleto</span>
                                                    )}
                                                </td>

                                                <td className="px-6 py-4 text-center">
                                                    {rec.status === 'active' ? (
                                                        <span className="bg-green-100 text-green-700 px-2.5 py-1 rounded-full text-xs font-bold inline-flex items-center">
                                                            <Check className="w-3 h-3 mr-1" /> Ativa
                                                        </span>
                                                    ) : (
                                                        <span className="bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full text-xs font-bold inline-flex items-center">
                                                            <Pause className="w-3 h-3 mr-1" /> Pausada
                                                        </span>
                                                    )}
                                                </td>

                                                <td className="px-6 py-4 text-center">
                                                    {rec.lembreteReajuste?.ativo ? (
                                                        due ? (
                                                            <button
                                                                onClick={() => openEditRecurrenceModal(rec)}
                                                                className={`px-2.5 py-1 rounded text-xs font-bold flex items-center mx-auto transition-all shadow-sm ${
                                                                    expired
                                                                        ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse'
                                                                        : 'bg-amber-500 text-white hover:bg-amber-600'
                                                                }`}
                                                                title="Clique para reajustar o valor da mensalidade"
                                                            >
                                                                <AlertTriangle className="w-3 h-3 mr-1" />
                                                                {expired ? 'Reajuste Vencido!' : 'Reajustar Agora'}
                                                            </button>
                                                        ) : (
                                                            <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded font-medium">
                                                                Reajuste em {new Date(rec.lembreteReajuste.dataReajuste).toLocaleDateString()}
                                                            </span>
                                                        )
                                                    ) : (
                                                        <span className="text-xs text-gray-400">Sem lembrete</span>
                                                    )}
                                                </td>

                                                <td className="px-6 py-4 text-center">
                                                    <div className="flex items-center justify-center gap-1">
                                                        <button
                                                            onClick={() => handleTriggerRecurrence(rec)}
                                                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                                            title="Emitir NFS-e deste mês agora"
                                                        >
                                                            <Play className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => openEditRecurrenceModal(rec)}
                                                            className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded"
                                                            title="Editar valor / dia / reajuste"
                                                        >
                                                            <Edit3 className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleToggleRecurrenceStatus(rec)}
                                                            className={`p-1.5 rounded ${rec.status === 'active' ? 'text-amber-600 hover:bg-amber-50' : 'text-green-600 hover:bg-green-50'}`}
                                                            title={rec.status === 'active' ? 'Pausar recorrência' : 'Retomar recorrência'}
                                                        >
                                                            {rec.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteRecurrence(rec._id)}
                                                            className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                                                            title="Cancelar recorrência definitivamente"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* MODAL DE EMISSÃO DE NFS-E & CRIAÇÃO DE RECORRÊNCIA */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden animate-fadeIn flex flex-col max-h-[92vh]">
                        {/* HEADER MODAL */}
                        <div className="bg-gradient-to-r from-blue-700 to-indigo-800 px-6 py-4 flex justify-between items-center shrink-0 text-white">
                            <div>
                                <h3 className="font-bold text-lg flex items-center">
                                    <FileText className="mr-2 w-5 h-5 text-blue-200" />
                                    Emissão de Nota Fiscal de Serviços (NFS-e Padrão Nacional)
                                </h3>
                                <p className="text-xs text-blue-100">
                                    Preencha os dados do serviço ou selecione um modelo salvo para preenchimento rápido.
                                </p>
                            </div>
                            <button
                                onClick={() => setModalOpen(false)}
                                className="text-blue-100 hover:text-white hover:bg-white/10 rounded-full p-1.5 transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* BODY MODAL */}
                        <div className="p-6 space-y-5 overflow-y-auto custom-scrollbar flex-1">
                            {/* MODELOS SALVOS */}
                            <div className="bg-blue-50/70 p-4 rounded-xl border border-blue-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                                <div className="flex-1">
                                    <label className="block text-xs font-bold text-blue-900 mb-1 flex items-center">
                                        <Sparkles className="w-3.5 h-3.5 mr-1 text-blue-600" />
                                        Carregar Modelo Salvo de Serviço
                                    </label>
                                    <select
                                        className="w-full p-2 text-sm border-blue-200 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 font-medium"
                                        onChange={handleModelSelect}
                                        defaultValue=""
                                    >
                                        <option value="" disabled>Selecione um modelo para preencher tudo automaticamente...</option>
                                        {(configDefaults.models || []).map((m, i) => (
                                            <option key={i} value={m.name}>{m.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex items-end">
                                    <button
                                        type="button"
                                        onClick={handleSaveAsModel}
                                        className="bg-white hover:bg-blue-100/50 text-blue-700 border border-blue-300 px-3.5 py-2 rounded-lg text-xs font-bold flex items-center transition-colors shadow-sm"
                                    >
                                        <Sparkles className="w-3.5 h-3.5 mr-1.5 text-blue-600" />
                                        Salvar como Novo Modelo
                                    </button>
                                </div>
                            </div>

                            {/* SELEÇÃO DE ATIVIDADE MUNICIPAL & TRIBUTAÇÃO NACIONAL (PRINT 2) */}
                            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-3">
                                <div className="pb-2 border-b">
                                    <label className="text-sm font-bold text-gray-800 flex items-center">
                                        Código de Atividade do Município & Tributação Nacional *
                                    </label>
                                    <p className="text-xs text-gray-500">
                                        A seleção da atividade municipal preenche automaticamente a Tributação Nacional, Item LC 116 e Código NBS.
                                    </p>
                                </div>

                                <div>
                                    <select
                                        className="w-full p-2.5 border rounded-lg text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 font-medium"
                                        value={form.codigoAtividadeMunicipal}
                                        onChange={e => handleActivitySelect(e.target.value)}
                                    >
                                        <option value="">Selecione a atividade municipal...</option>
                                        {atividadesDisponiveis.map(act => (
                                            <option key={act.codigoAtividadeMunicipal} value={act.codigoAtividadeMunicipal}>
                                                {formatarLabelAtividade(act)}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* CARDS DOS CÓDIGOS VINCULADOS */}
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-1">
                                    <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                                        <label className="block text-[11px] font-bold text-gray-500">Item LC 116/2003 *</label>
                                        <input
                                            type="text"
                                            className="w-full bg-transparent font-bold text-gray-800 text-sm outline-none"
                                            placeholder="Ex: 01.03"
                                            value={form.itemLc116}
                                            onChange={e => setForm({ ...form, itemLc116: e.target.value })}
                                        />
                                    </div>
                                    <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                                        <label className="block text-[11px] font-bold text-gray-500">Tributação Nacional *</label>
                                        <input
                                            type="text"
                                            className="w-full bg-transparent font-bold text-gray-800 text-sm outline-none"
                                            placeholder="Ex: 01.03.01"
                                            value={form.tributacaoNacional}
                                            onChange={e => setForm({ ...form, tributacaoNacional: e.target.value })}
                                        />
                                    </div>
                                    <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                                        <label className="block text-[11px] font-bold text-gray-500">Código NBS *</label>
                                        <input
                                            type="text"
                                            className="w-full bg-transparent font-bold text-gray-800 text-sm outline-none"
                                            placeholder="Ex: 1.0103.11.00"
                                            value={form.codigoNbs}
                                            onChange={e => setForm({ ...form, codigoNbs: e.target.value })}
                                        />
                                    </div>
                                    <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                                        <label className="block text-[11px] font-bold text-gray-500">Cód. IBGE Incidência *</label>
                                        <input
                                            type="text"
                                            className="w-full bg-transparent font-bold text-gray-800 text-sm outline-none font-mono"
                                            placeholder={configDefaults.codMun || "3301702"}
                                            value={form.municipioIncidencia || configDefaults.codMun || ''}
                                            onChange={e => setForm({ ...form, municipioIncidencia: e.target.value })}
                                            title="Código IBGE do município de prestação. Padrão carregado de Configurações NFS-e."
                                        />
                                    </div>
                                    <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                                        <label className="block text-[11px] font-bold text-gray-500">Alíquota ISS (%)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="w-full bg-transparent font-bold text-gray-800 text-sm outline-none"
                                            placeholder="2.00"
                                            value={form.aliquota}
                                            onChange={e => setForm({ ...form, aliquota: parseFloat(e.target.value) || 0 })}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* TOMADOR, VALOR, DATA COMPETÊNCIA E HORA DA CRIAÇÃO */}
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                                <div className="md:col-span-4 relative">
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Tomador dos Serviços (Cliente) *</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Search className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            className="w-full pl-9 p-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50 text-gray-900"
                                            placeholder="Busque pelo Nome ou CNPJ do cliente..."
                                            value={clientSearch}
                                            onChange={e => {
                                                setClientSearch(e.target.value);
                                                setShowClientDropdown(true);
                                            }}
                                            onFocus={() => setShowClientDropdown(true)}
                                        />
                                        {form.payerId && (
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                                <CheckCircle className="h-4 w-4 text-green-500" />
                                            </div>
                                        )}
                                    </div>

                                    {showClientDropdown && (
                                        <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                            {companies
                                                .filter(c =>
                                                    c.name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
                                                    (c.cnpj && c.cnpj.includes(clientSearch))
                                                )
                                                .map(c => (
                                                    <div
                                                        key={c._id}
                                                        className="p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 flex justify-between items-center"
                                                        onClick={() => {
                                                            setForm({ ...form, payerId: c._id });
                                                            setClientSearch(c.name);
                                                            setShowClientDropdown(false);
                                                        }}
                                                    >
                                                        <div>
                                                            <div className="font-bold text-gray-800">{c.name}</div>
                                                            <div className="text-xs text-gray-500">{c.cnpj ? `CNPJ: ${c.cnpj}` : 'Sem CNPJ cadastrado'}</div>
                                                        </div>
                                                        {form.payerId === c._id && <CheckCircle className="w-4 h-4 text-green-500" />}
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                    {showClientDropdown && (
                                        <div className="fixed inset-0 z-10" onClick={() => setShowClientDropdown(false)} />
                                    )}
                                </div>

                                <div className="md:col-span-3">
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Valor do Serviço (R$) *</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-2.5 text-gray-500 font-bold">R$</span>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="w-full pl-10 p-2.5 border rounded-lg font-bold text-gray-900 focus:ring-2 focus:ring-blue-500 text-lg"
                                            value={form.amount}
                                            onChange={e => setForm({ ...form, amount: e.target.value })}
                                            placeholder="0,00"
                                        />
                                    </div>
                                </div>

                                <div className="md:col-span-3">
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Data Competência *</label>
                                    <input
                                        type="date"
                                        className="w-full p-2.5 border rounded-lg font-bold text-gray-900 focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                                        value={form.dataCompetencia || getBrasiliaDate()}
                                        onChange={e => setForm({ ...form, dataCompetencia: e.target.value })}
                                        title="Data de ocorrência do fato gerador do serviço"
                                    />
                                    <p className="text-[11px] text-gray-500 mt-1">Data do fato gerador</p>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Hora da Criação *</label>
                                    <input
                                        type="time"
                                        step="1"
                                        className="w-full p-2.5 border rounded-lg font-bold text-gray-900 focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                                        value={form.horaCriacao || getCurrentBrasiliaTime()}
                                        onChange={e => setForm({ ...form, horaCriacao: e.target.value })}
                                        title="Horário de criação da nota fiscal"
                                    />
                                    <p className="text-[11px] text-blue-600 font-semibold mt-1">Horário de criação da nota</p>
                                </div>

                                <div className="md:col-span-12">
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Discriminação dos Serviços Prestados *</label>
                                    <textarea
                                        rows={3}
                                        className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                        placeholder="Ex: Prestação de serviços de suporte técnico e desenvolvimento de software..."
                                        value={form.description}
                                        onChange={e => setForm({ ...form, description: e.target.value })}
                                    />
                                </div>
                            </div>

                            {/* TRIBUTAÇÃO FEDERAL (PIS / COFINS) (PRINT 4) */}
                            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                                <div className="pb-2 mb-3 border-b flex items-center justify-between">
                                    <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center">
                                        <FileText className="w-4 h-4 mr-1.5 text-blue-600" />
                                        Tributação Federal (PIS / COFINS)
                                    </h4>
                                    <span className="text-[11px] text-gray-500 font-mono">
                                        Total PIS/COFINS: R$ {(formPisVal + formCofinsVal).toFixed(2)}
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                                    <div className="md:col-span-6">
                                        <label className="block text-xs font-bold text-gray-600 mb-1">Situação Tributária PIS/COFINS (CST 00 a 99)</label>
                                        <select
                                            className="w-full p-2 border rounded-lg text-xs bg-gray-50 font-mono"
                                            value={form.cstPisCofins}
                                            onChange={e => setForm({ ...form, cstPisCofins: e.target.value })}
                                        >
                                            {CST_PIS_COFINS_OPTIONS.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="md:col-span-3 bg-gray-50 p-2 rounded-lg border">
                                        <div className="flex justify-between items-center mb-1">
                                            <label className="text-[11px] font-bold text-gray-600">Alíquota PIS (%)</label>
                                            <span className="text-[11px] font-bold text-blue-700">R$ {formPisVal.toFixed(2)}</span>
                                        </div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="w-full p-1.5 border rounded text-xs font-bold"
                                            value={form.aliquotaPis}
                                            onChange={e => setForm({ ...form, aliquotaPis: parseFloat(e.target.value) || 0 })}
                                        />
                                    </div>

                                    <div className="md:col-span-3 bg-gray-50 p-2 rounded-lg border">
                                        <div className="flex justify-between items-center mb-1">
                                            <label className="text-[11px] font-bold text-gray-600">Alíquota COFINS (%)</label>
                                            <span className="text-[11px] font-bold text-blue-700">R$ {formCofinsVal.toFixed(2)}</span>
                                        </div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="w-full p-1.5 border rounded text-xs font-bold"
                                            value={form.aliquotaCofins}
                                            onChange={e => setForm({ ...form, aliquotaCofins: parseFloat(e.target.value) || 0 })}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* REFORMA TRIBUTÁRIA: CBS & IBS */}
                            <div className="bg-indigo-50/60 p-4 rounded-xl border border-indigo-100 shadow-sm">
                                <div className="flex flex-wrap items-center justify-between mb-3 pb-2 border-b border-indigo-100 gap-2">
                                    <div className="flex items-center text-xs font-bold text-indigo-950 uppercase tracking-wider">
                                        <ShieldCheck className="w-4 h-4 mr-1.5 text-indigo-600" />
                                        Reforma Tributária: CBS (Federal) & IBS (Estadual + Municipal)
                                    </div>
                                    <span className="text-[11px] bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded font-bold">
                                        NT 009/2026 - Padrão Nacional
                                    </span>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <div className="bg-white p-2.5 rounded-lg border border-indigo-200">
                                        <div className="flex justify-between items-center mb-1">
                                            <label className="text-xs font-bold text-indigo-900">Alíq. CBS (%)</label>
                                            <span className="text-[11px] font-bold text-indigo-700">R$ {formCbsVal.toFixed(2)}</span>
                                        </div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="w-full p-1.5 border rounded text-xs font-bold text-indigo-900"
                                            value={form.aliquotaCbs}
                                            onChange={e => setForm({ ...form, aliquotaCbs: parseFloat(e.target.value) || 0 })}
                                        />
                                    </div>

                                    <div className="bg-white p-2.5 rounded-lg border border-indigo-200">
                                        <label className="block text-xs font-bold text-indigo-900 mb-1">IBS Estadual (%)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="w-full p-1.5 border rounded text-xs"
                                            value={form.aliquotaIbsEst}
                                            onChange={e => setForm({ ...form, aliquotaIbsEst: parseFloat(e.target.value) || 0 })}
                                        />
                                    </div>

                                    <div className="bg-white p-2.5 rounded-lg border border-indigo-200">
                                        <label className="block text-xs font-bold text-indigo-900 mb-1">IBS Municipal (%)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="w-full p-1.5 border rounded text-xs"
                                            value={form.aliquotaIbsMun}
                                            onChange={e => setForm({ ...form, aliquotaIbsMun: parseFloat(e.target.value) || 0 })}
                                        />
                                    </div>

                                    <div className="bg-indigo-100/80 p-2.5 rounded-lg border border-indigo-200 flex flex-col justify-center">
                                        <div className="text-[11px] font-bold text-indigo-900">Total IBS Apurado</div>
                                        <div className="text-sm font-black text-indigo-950">R$ {formIbsVal.toFixed(2)}</div>
                                        <div className="text-[10px] text-indigo-700 font-medium">({formIbsAliqTotal.toFixed(2)}% total)</div>
                                    </div>
                                </div>
                            </div>

                            {/* RECORRÊNCIA MENSAL & LEMBRETE DE REAJUSTE */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200 shadow-sm space-y-3">
                                <div className="flex items-center justify-between">
                                    <label className="flex items-center space-x-2.5 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={form.isRecurrent}
                                            onChange={e => setForm({ ...form, isRecurrent: e.target.checked })}
                                            className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-black text-blue-900 flex items-center">
                                            <Repeat className="w-4 h-4 mr-1.5 text-blue-600" />
                                            Ativar Emissão Recorrente Todo Mês (Prazo Indeterminado)
                                        </span>
                                    </label>
                                    {form.isRecurrent && (
                                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-bold">
                                            Recorrente até cancelamento
                                        </span>
                                    )}
                                </div>

                                {form.isRecurrent && (
                                    <div className="pt-3 border-t border-blue-200/60 grid grid-cols-1 md:grid-cols-12 gap-3 animate-fadeIn">
                                        <div className="md:col-span-3">
                                            <label className="block text-xs font-bold text-blue-900 mb-1">Dia do Mês para Emissão *</label>
                                            <div className="relative">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="28"
                                                    className="w-full p-2 border rounded-lg text-sm bg-white font-bold text-center"
                                                    value={form.dayOfMonth}
                                                    onChange={e => setForm({ ...form, dayOfMonth: parseInt(e.target.value) || 1 })}
                                                />
                                            </div>
                                            <p className="text-[10px] text-blue-600 mt-1">Ex: Todo dia {form.dayOfMonth} do mês.</p>
                                        </div>

                                        <div className="md:col-span-9 bg-white p-3 rounded-lg border border-blue-100 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <label className="flex items-center space-x-2 text-xs font-bold text-gray-700 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={form.lembreteReajuste?.ativo}
                                                        onChange={e => setForm({
                                                            ...form,
                                                            lembreteReajuste: { ...form.lembreteReajuste, ativo: e.target.checked }
                                                        })}
                                                        className="rounded text-indigo-600"
                                                    />
                                                    <span className="flex items-center">
                                                        <Bell className="w-3.5 h-3.5 mr-1 text-indigo-600" />
                                                        Lembrete de Reajuste de Mensalidade
                                                    </span>
                                                </label>
                                                <span className="text-[11px] text-gray-400">Destaque visual na data do reajuste</span>
                                            </div>

                                            {form.lembreteReajuste?.ativo && (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1 animate-fadeIn">
                                                    <div>
                                                        <label className="block text-[11px] font-bold text-gray-600 mb-1">Data Prevista do Próximo Reajuste</label>
                                                        <input
                                                            type="date"
                                                            className="w-full p-1.5 border rounded text-xs bg-gray-50 focus:bg-white"
                                                            value={form.lembreteReajuste.dataReajuste}
                                                            onChange={e => setForm({
                                                                ...form,
                                                                lembreteReajuste: { ...form.lembreteReajuste, dataReajuste: e.target.value }
                                                            })}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-[11px] font-bold text-gray-600 mb-1">Observação do Reajuste</label>
                                                        <input
                                                            type="text"
                                                            className="w-full p-1.5 border rounded text-xs bg-gray-50 focus:bg-white"
                                                            placeholder="Ex: Reajuste anual pelo IPCA"
                                                            value={form.lembreteReajuste.observacao}
                                                            onChange={e => setForm({
                                                                ...form,
                                                                lembreteReajuste: { ...form.lembreteReajuste, observacao: e.target.value }
                                                            })}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* RODAPÉ MODAL */}
                        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t shrink-0">
                            <button
                                type="button"
                                onClick={() => setModalOpen(false)}
                                className="px-5 py-2.5 text-gray-600 hover:bg-gray-200 rounded-lg font-bold text-sm transition-colors"
                            >
                                Cancelar
                            </button>

                            <button
                                type="button"
                                onClick={handleEmit}
                                disabled={emitting}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-bold flex items-center shadow-lg transition-all text-sm disabled:opacity-50"
                            >
                                {emitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                                {emitting ? 'Emitindo...' : 'Emitir'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE EDIÇÃO DE RECORRÊNCIA & REAJUSTE DE MENSALIDADE */}
            {editingRecurrence && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-fadeIn">
                        <div className="bg-gradient-to-r from-indigo-700 to-blue-700 px-6 py-4 flex justify-between items-center text-white">
                            <div>
                                <h3 className="font-bold text-base flex items-center">
                                    <Edit3 className="w-4 h-4 mr-2" />
                                    Editar Recorrência & Reajuste de Mensalidade
                                </h3>
                                <p className="text-xs text-indigo-100 mt-0.5">
                                    Cliente: <strong>{editingRecurrence.payerName}</strong>
                                </p>
                            </div>
                            <button
                                onClick={() => setEditingRecurrence(null)}
                                className="text-indigo-200 hover:text-white rounded-full p-1"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Novo Valor da Mensalidade (R$) *</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-2.5 text-gray-500 font-bold">R$</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="w-full pl-10 p-2.5 border rounded-lg font-bold text-gray-900 focus:ring-2 focus:ring-indigo-500 text-lg"
                                        value={editingRecurrence.amount}
                                        onChange={e => setEditingRecurrence({ ...editingRecurrence, amount: e.target.value })}
                                        placeholder="0,00"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Dia da Emissão Todo Mês (1 a 28) *</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="28"
                                    className="w-full p-2.5 border rounded-lg text-sm font-bold text-gray-800"
                                    value={editingRecurrence.dayOfMonth}
                                    onChange={e => setEditingRecurrence({ ...editingRecurrence, dayOfMonth: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Descrição / Discriminação do Serviço</label>
                                <input
                                    type="text"
                                    className="w-full p-2.5 border rounded-lg text-sm text-gray-800"
                                    value={editingRecurrence.description}
                                    onChange={e => setEditingRecurrence({ ...editingRecurrence, description: e.target.value })}
                                />
                            </div>

                            {/* BLOCO DE REAJUSTE DE MENSALIDADE */}
                            <div className="bg-amber-50/70 p-4 rounded-xl border border-amber-200 space-y-3">
                                <div className="flex items-center justify-between">
                                    <label className="flex items-center space-x-2 text-xs font-bold text-amber-900 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={editingRecurrence.lembreteAtivo}
                                            onChange={e => setEditingRecurrence({ ...editingRecurrence, lembreteAtivo: e.target.checked })}
                                            className="rounded text-amber-600 focus:ring-amber-500"
                                        />
                                        <span>Ativar Lembrete de Reajuste de Mensalidade</span>
                                    </label>
                                </div>

                                {editingRecurrence.lembreteAtivo && (
                                    <div className="space-y-3 pt-2 border-t border-amber-200 animate-fadeIn">
                                        <div>
                                            <div className="flex justify-between items-center mb-1">
                                                <label className="block text-xs font-bold text-amber-900">Data do Próximo Reajuste</label>
                                                <button
                                                    type="button"
                                                    onClick={() => setEditingRecurrence({ ...editingRecurrence, dataReajuste: defaultNextYear() })}
                                                    className="text-[11px] text-indigo-700 hover:underline font-bold"
                                                >
                                                    + 12 meses (1 ano)
                                                </button>
                                            </div>
                                            <input
                                                type="date"
                                                className="w-full p-2 border rounded-lg text-sm bg-white font-medium"
                                                value={editingRecurrence.dataReajuste}
                                                onChange={e => setEditingRecurrence({ ...editingRecurrence, dataReajuste: e.target.value })}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-bold text-amber-900 mb-1">Observações do Reajuste</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 border rounded-lg text-sm bg-white"
                                                placeholder="Ex: Reajustado conforme IPCA acumulado..."
                                                value={editingRecurrence.observacao}
                                                onChange={e => setEditingRecurrence({ ...editingRecurrence, observacao: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-2 border-t">
                            <button
                                type="button"
                                onClick={() => setEditingRecurrence(null)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg text-sm font-bold transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveEditedRecurrence}
                                disabled={savingRecurrence}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-lg text-sm font-bold flex items-center shadow transition-all disabled:opacity-50"
                            >
                                {savingRecurrence ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                                Salvar Alterações
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 🆕 MODAL: VISUALIZADOR DANFSE v2.0 OFICIAL */}
            {danfseModalNote && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-5">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[92vh] flex flex-col overflow-hidden border border-gray-300">
                        {/* Header do Visualizador */}
                        <div className="bg-gray-900 text-white px-6 py-3.5 flex flex-wrap justify-between items-center gap-3 shrink-0">
                            <div className="flex items-center gap-2">
                                <FileText className="w-5 h-5 text-blue-400" />
                                <span className="font-bold text-base">
                                    DANFSe v2.0 - NFS-e Nº {danfseModalNote.number || danfseModalNote.numeroDps}
                                </span>
                                {danfseModalNote.status === 'canceled' && (
                                    <span className="bg-red-600 text-white text-xs px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider">
                                        Cancelada
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        const win = window.open(`/api/nfse/${danfseModalNote._id}/danfse?token=${localStorage.getItem('token')}`, '_blank');
                                        if (win) win.focus();
                                    }}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors shadow-sm"
                                    title="Abrir em Nova Aba"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" /> Abrir em Nova Aba
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleDownloadXml(danfseModalNote)}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors shadow-sm"
                                    title="Baixar XML Oficial"
                                >
                                    <Download className="w-3.5 h-3.5" /> Baixar XML
                                </button>
                                <a
                                    href={`/api/nfse/${danfseModalNote._id}/pdf?token=${localStorage.getItem('token')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors shadow-sm"
                                    title="Baixar DANFSe em formato PDF oficial"
                                >
                                    <Download className="w-3.5 h-3.5" /> Baixar PDF
                                </a>
                                <button
                                    type="button"
                                    onClick={() => {
                                        const iframe = document.getElementById('danfse-viewer-iframe');
                                        if (iframe && iframe.contentWindow) {
                                            iframe.contentWindow.print();
                                        }
                                    }}
                                    className="bg-white text-gray-900 hover:bg-gray-100 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors shadow-sm"
                                    title="Imprimir / Salvar em PDF"
                                >
                                    <Printer className="w-3.5 h-3.5" /> Imprimir
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setDanfseModalNote(null)}
                                    className="text-gray-400 hover:text-white p-1 rounded-lg transition-colors ml-2"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Iframe que renderiza o DANFSe v2.0 nativo */}
                        <div className="flex-1 bg-gray-100 p-2 overflow-hidden">
                            <iframe
                                id="danfse-viewer-iframe"
                                src={`/api/nfse/${danfseModalNote._id}/danfse?token=${localStorage.getItem('token')}`}
                                className="w-full h-full border-0 rounded-lg shadow bg-white"
                                title="Visualizador DANFSe v2.0"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* 🆕 MODAL: CANCELAMENTO DE NFS-e */}
            {cancelModalNote && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-red-200">
                        <div className="bg-red-50 p-6 border-b border-red-100 flex items-start gap-4">
                            <div className="p-3 bg-red-100 text-red-600 rounded-xl">
                                <Ban className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">
                                    Cancelar NFS-e Nº {cancelModalNote.number || cancelModalNote.numeroDps}
                                </h3>
                                <p className="text-xs text-gray-500 mt-1">
                                    Tomador: <b>{cancelModalNote.payerId?.name || 'Cliente'}</b> | Valor: R$ {cancelModalNote.amount?.toFixed(2)}
                                </p>
                            </div>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">
                                    Código do Motivo (Padrão Nacional SEFIN)
                                </label>
                                <select
                                    value={cancelCode}
                                    onChange={e => setCancelCode(e.target.value)}
                                    className="w-full border p-2.5 rounded-lg text-sm bg-white outline-none focus:border-red-500"
                                >
                                    <option value="1">1 - Erro na emissão</option>
                                    <option value="2">2 - Serviço não prestado</option>
                                    <option value="3">3 - Duplicidade da nota</option>
                                    <option value="9">9 - Outros motivos</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">
                                    Justificativa do Cancelamento <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    className="w-full border p-3 rounded-lg text-sm outline-none focus:border-red-500"
                                    rows="3"
                                    placeholder="Descreva detalhadamente a justificativa para o cancelamento desta NFS-e..."
                                    value={cancelReason}
                                    onChange={e => setCancelReason(e.target.value)}
                                />
                                <span className="text-[11px] text-gray-400">Mínimo de 5 caracteres.</span>
                            </div>
                        </div>

                        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-2 border-t">
                            <button
                                type="button"
                                onClick={() => setCancelModalNote(null)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg text-sm font-bold transition-colors"
                            >
                                Voltar
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmCancel}
                                disabled={canceling}
                                className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-lg text-sm font-bold flex items-center shadow transition-all disabled:opacity-50"
                            >
                                {canceling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />}
                                Confirmar Cancelamento
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 🆕 MODAL: CARTA DE CORREÇÃO / RETIFICAÇÃO */}
            {correctionModalNote && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-200">
                        <div className="bg-blue-50 p-6 border-b border-blue-100 flex items-start gap-4">
                            <div className="p-3 bg-blue-100 text-blue-600 rounded-xl">
                                <Edit3 className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">
                                    Carta de Correção / Retificação - NFS-e Nº {correctionModalNote.number || correctionModalNote.numeroDps}
                                </h3>
                                <p className="text-xs text-gray-500 mt-1">
                                    A retificação de NFS-e não permite alterar valores ou tributos diretamente. Para alterar valores, cancele e emita nova nota.
                                </p>
                            </div>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">
                                    Descrição da Retificação / Dados Complementares <span className="text-blue-500">*</span>
                                </label>
                                <textarea
                                    className="w-full border p-3 rounded-lg text-sm outline-none focus:border-blue-500"
                                    rows="4"
                                    placeholder="Informe o texto retificado ou dados complementares do serviço..."
                                    value={correctionText}
                                    onChange={e => setCorrectionText(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-2 border-t">
                            <button
                                type="button"
                                onClick={() => setCorrectionModalNote(null)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg text-sm font-bold transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmCorrection}
                                disabled={savingCorrection}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-bold flex items-center shadow transition-all disabled:opacity-50"
                            >
                                {savingCorrection ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                                Salvar Retificação
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotasFiscaisScreen;
